<template>
  <cropper
    v-if="src && showCropper"
    ref="cropperRef"
    :src="src"
    :default-transforms="defaultTransforms"
    :resize-image="{ wheel: false }"
    :stencil-props="{
      aspectRatio,
      movable: false,
      resizable: false,
    }"
    :default-size="getDefaultSize"
    :size-restrictions-algorithm="sizeRestriction"
    @ready="refreshCropper"
  />
</template>

<script setup lang="ts">
  import { ref, onUnmounted, computed } from 'vue';
  import { Cropper } from 'vue-advanced-cropper';
  import type { ICropCoords } from '@shared/models/crop';
  import { getOrderXmlCropDimensions, maintainCropCenterFromCoords, normalizeRotation } from '@shared/services/crop';
  import type { MCropperResult } from '@shared/components/MCropper/models';

  export interface Props {
    src: string;
    crop?: ICropCoords;
    aspectRatio: number;
    rotation?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    crop: undefined,
    rotation: 0,
  });
  const showCropper = ref<boolean>(true);
  const cropperRef = ref<InstanceType<typeof Cropper> | null>(null);
  const ready = ref<boolean>(false);

  const getResult = (): MCropperResult | null => {
    if (!cropperRef.value) return null;
    const result = cropperRef.value.getResult();

    const normalizedRotation = normalizeRotation(result.image.transforms.rotate);
    result.image.transforms.rotate = normalizedRotation;

    const ret = {
      ...result,
      orderXmlCoordinates: getOrderXmlCropDimensions(result.coordinates, result.image, normalizedRotation),
    };

    return ret;
  };

  interface IDefaultSizeProps {
    visibleArea: { left: number; top: number; width: number; height: number } | null;
    imageSize: { width: number; height: number };
    stencilRatio: { minimum: number; maximum: number };
    sizeRestrictions: { minWidth: number; minHeight: number; maxWidth: number; maxHeight: number };
  }

  const getDefaultSize = ({ visibleArea, imageSize, stencilRatio, sizeRestrictions }: IDefaultSizeProps) => {
    return {
      width: sizeRestrictions.maxWidth ?? imageSize.width,
      height: sizeRestrictions.maxHeight ?? imageSize.height,
    };
  };

  onUnmounted(() => {
    cropperRef.value?.reset();
    showCropper.value = false;
  });

  /**
   * Calculate crop dimensions that fit INSIDE the soft crop bounds while matching the target aspect ratio.
   * This prevents the crop from ever extending outside the user's soft crop region.
   *
   * The logic ensures we always shrink one dimension to fit within bounds:
   * - If target is wider than soft crop: keep width, shrink height
   * - If target is taller than soft crop: keep height, shrink width
   */
  const calculateCropToFitInside = (
    targetAspectRatio: number,
    softCropWidth: number,
    softCropHeight: number,
  ): { width: number; height: number } => {
    const softCropAspectRatio = softCropWidth / softCropHeight;

    let newWidth: number;
    let newHeight: number;

    if (targetAspectRatio > softCropAspectRatio) {
      // Target is WIDER - use full soft crop width, calculate reduced height
      newWidth = softCropWidth;
      newHeight = softCropWidth / targetAspectRatio;
    } else {
      // Target is TALLER or equal - use full soft crop height, calculate reduced width
      newHeight = softCropHeight;
      newWidth = softCropHeight * targetAspectRatio;
    }

    return { width: newWidth, height: newHeight };
  };

  const refreshCropper = () => {
    if (cropperRef.value && props.crop) {
      // Calculate crop dimensions that fit INSIDE the soft crop while matching target aspect ratio
      const { width, height } = calculateCropToFitInside(props.aspectRatio, props.crop.width, props.crop.height);

      cropperRef.value.refresh();
      // Set coordinates with calculated size, centered on the soft crop center
      cropperRef.value.setCoordinates([{ width, height }, maintainCropCenterFromCoords(props.crop, 0)]);

      // Wait for the cropper to process the new coordinates before signaling ready
      setTimeout(() => {
        ready.value = true;
      }, 300);
    } else if (cropperRef.value) {
      cropperRef.value.refresh();
      ready.value = true;
    }
  };
  const sizeRestriction = ({
    maxWidth,
    maxHeight,
    imageSize,
  }: {
    maxWidth: number;
    maxHeight: number;
    imageSize: { width: number; height: number };
  }) => ({
    // Allow very small crops - don't force minimum sizes that would extend outside soft crop bounds
    minWidth: 1,
    minHeight: 1,
    maxWidth,
    maxHeight,
  });

  const defaultTransforms = computed(() => ({
    rotate: props.rotation ?? 0,
  }));

  defineExpose({
    getResult,
    ready,
  });
</script>
